import {currentUser} from '@/services/FirestoreService';
import {MetricService} from '@/services/MetricService.js';
import {MapService} from '@/services/MapService';
import {Graphic} from '@/services/GraphicService/Graphic';
import {EventService} from '@/services/EventService';
import {SubscriptionManager} from '@northpoint-development/labs-common-core';

/**
 * @typedef {Record<string, any> & {geom: Array<google.maps.Polygon>}} Parcel
 */

const cachedToken = {
  /** @type {string} */
  token: undefined,
  /** @type {number} */
  expiration: undefined,
  /** @type {Promise<void> | false} */
  inProgress: false,
};

let token;
/**
 * @return {Promise<string>} the token
 */
const retrieveReportAllToken = async () => {
  if (cachedToken.inProgress) {
    await cachedToken.inProgress;
  }

  if (cachedToken.token && cachedToken.expiration && cachedToken.expiration > Date.now()) {
    return cachedToken.token;
  }

  try {
    /** @type {() => void} */
    let done;
    cachedToken.inProgress = new Promise((resolve) => (done = resolve));

    const idToken = await currentUser().getIdToken(true);
    const urlEnv = process.env.VUE_APP_GCP_ENV === 'production' ? '' : '-staging';

    const response = await fetch(
      `https://us-central1-northpoint-production.cloudfunctions.net/retrieveMapsToken${urlEnv}?retrieve=reportAllToken`,
      {method: 'GET', headers: {Authorization: `Bearer ${idToken}`}},
    );
    token = (await response.json()).token;

    cachedToken.token = token;
    cachedToken.expiration = Date.now() + 1000 * 60 * 60 * 1; // Report All tokens expire after 1 hour
    done();
    cachedToken.inProgress = false;
    return token;
  } catch (error) {
    error.message = `Failed to get ReportAll server token: ${error.message}`;
    throw error;
  }
};

/**
 * Asynchronously constructs a new REPGraphic object.
 * @async
 * @return {Promise<REPGraphic>} The constructed graphic.
 */
async function createREPGraphic() {
  if (!document.getElementById('reportAllScript')) {
    await _reportAllScriptLoader();
  }
  return new REPGraphic();
}

/**
 * Loads the parcel tracker script and resolves the loader promise when the DOM onload event fires for the injected
 * script. Should be invoked whenever a new REPGraphic is created.
 * @private
 * @async
 * @type {Function}
 */
const _reportAllScriptLoader = async () => {
  // Use a promise as a sort of mutex to prevent multiple simultaneous attempts to load the REP script.
  let resolver;
  const reportAllPromise = new Promise((resolve) => resolver = resolve);
  // Get parcel API token from the token server.
  const token = await retrieveReportAllToken();
  // create a new script element set to the ReportAll script API endpoint
  const reportAllScript = document.createElement('script');
  reportAllScript.id = 'reportAllScript';
  reportAllScript.setAttribute('src', `https://reportallusa.com/overlay/js.php?v=8&map=Google&token=${token}`);
  document.head.appendChild(reportAllScript);
  // Resolve the promise so other callers know REP is ready.
  reportAllScript.onload = () => resolver();
  await reportAllPromise;
  setTimeout(() => {
    // Guard against environments without window (e.g., CI/unit tests) when the timeout fires.
    if (typeof window === 'undefined' || !window.REP?.Layer?.Google?.ParcelTilesRequestedCount) return;
    // Update the user's metrics to reflect the tiles initially loaded.
    // This has to be in a timeout or else `ParcelTilesRequestedCount` returns zero.
    MetricService.increaseMetric('reportAllTiles', window.REP.Layer.Google.ParcelTilesRequestedCount());
  }, 1000);
};

/** @type {Map<string, Parcel>} */
const _cachedParcels = new Map();
/** @type {string | null} */
let _highlightedParcel = null;

/**
 * Class Representing a REP (Report) Graphic
 * @property {boolean} _clickable Whether the graphic is clickable
 * @property {boolean} _render Whether the graphic is currently rendered
 */
class REPGraphic extends Graphic {
  /**
   * Create an REPGraphic
   */
  constructor() {
    super();
    /** @type {boolean} Whether the graphic is clickable */
    this._clickable = false;
    /** @type {boolean} Whether the graphic is currently rendered */
    this._render = false;
    /** @type {SubscriptionManager} */
    this.subscriptions = new SubscriptionManager();
    // Handle clicking parcels, emit parcel_clicked event with the parcels at the clicked location.
    this.addHandler('click', 'parcelClick', async (event) => {
      const parcels = await this.getParcels(event.latLng);
      this.subscriptions.publish('parcel_click', parcels);
    });
  }

  /**
   * Adds a new event handler for a ReportAll graphic.
   * @param {string} eventType The type of event for which to invoke the handler.
   * @param {string} handlerName A name identifying this handler for later removal.
   * @param {Function} handler The handler to be invoked.
   */
  addHandler(eventType, handlerName, handler) {
    // Wrap the handler in logic to suppress the listener if this graphic is not clickable.
    const wrapped = (eventType !== 'click') ? handler : (...args) => {
      if (this._clickable && this._render) return handler(...args);
    };
    EventService.addLayerHandler(`${this.uuid}-parcel-click`, handlerName, wrapped);
  }

  /**
   * Removes an event listener from this graphic.
   * @param {Function} remover function returned by addListener
   */
  removeListener(remover) {
    remover();
  }

  /**
   * Sets whether this graphic can be clicked.
   * @param {boolean} clickable If `true`, this overlay will be clickable on the map; otherwise it will not.
   */
  setClickable(clickable) {
    this._clickable = clickable;
  }

  /**
   * Attaches the graphic to a map or removes it from a map.
   * @param {google.maps.Map|null} map The map to attach this graphic to,
   * or `null` to detach the graphic from its current map.
   */
  setMap(map) {
    const overlayMapTypes = MapService.getMap().overlayMapTypes;
    if (!map) {
      let parcelIndex = -1;
      overlayMapTypes?.forEach((overlay, index) => {
        if (overlay?.name === 'Parcels') {
          parcelIndex = index;
        }
      });
      // There is no way in the ReportAll script to remove the graphic. Examining the source reveals that it is
      // just adding the overlay to the overlayMapTypes array on the map, and it can be removed from there.
      overlayMapTypes.removeAt(parcelIndex);
      this.setClickable(false);
      this._render = false;
      if (_highlightedParcel) {
        this.unHighlightParcel(_highlightedParcel);
      }
      return;
    }
    // Only initialize the map if it does not already exist
    if (this._render) return;
    window.REP.Layer.Google.Initialize(map, {Return_Buildings: true, Parcels_OutlineColor: '#00ffef'});
    this._render = true;
  }

  tokenExpired() {
    return (!cachedToken.token || !cachedToken.expiration || cachedToken.expiration <= Date.now());
  }

  async identifyPoint(latLng) {
    MetricService.increaseMetric('reportAllParcels');
    return await new Promise((resolve, reject) => {
      window.REP.Layer.Google.IdentifyByPoint(
        MapService.getMap(),
        latLng,
        (response) => resolve(response),
        (error) => reject(error),
      );
    });
  }

  /**
   * Get the parcels at the given latLng. By default the parcels will be cached for use when highlighting, but this can
   * be overridden by setting `skipCache` to `true` if you only need the geometry once.
   * @param {google.maps.LatLng} latLng
   * @param {boolean} [skipCache=false]
   * @return {Promise<Parcel[]>} The parcels at the given latLng
   */
  async getParcels(latLng, skipCache = false) {
    let parcels;
    try {
      parcels = await this.identifyPoint(latLng);
    } catch (error) {
      console.error('Failed to identify parcels:', error);
      return [];
    }
    if (!parcels?.results?.length) return [];

    if (!skipCache) {
      _cachedParcels.clear();
    }

    // As of updating reportAll API version, latitude and longitude are no longer available
    // on the parcel. To remedy this, make the location of the parcels wherever the click event occurred
    parcels.results.forEach((parcel) => {
      parcel.location = latLng;
      if (!skipCache) {
        _cachedParcels.set(parcel.robust_id, parcel);
      }
    });
    return parcels.results;
  }

  highlightParcel(parcelId) {
    const parcel = _cachedParcels.get(parcelId);
    if (!parcel) return;
    parcel.geom.forEach((poly) => {
      poly.setOptions({
        fillColor: 'rgb(144,238,144)',
        strokeColor: 'rgb(200,0,0)',
      });
      poly.setMap(MapService.getMap());
      poly.addListener('click', () => this.setParcelHighlight(parcelId, false));
    });
    _highlightedParcel = parcelId;
  }

  unHighlightParcel(parcelId) {
    const parcel = _cachedParcels.get(parcelId);
    if (!parcel) return;
    parcel.geom.forEach((poly) => poly.setMap(null));
    _highlightedParcel = null;
  }

  setParcelHighlight(parcelId, state) {
    if (state) {
      this.highlightParcel(parcelId);
    } else {
      this.unHighlightParcel(parcelId);
    }
  }

  setHighlight(state) {
    if (!_highlightedParcel) return;
    this.setParcelHighlight(_highlightedParcel, state);
  }

  subscribe(eventName, callback) {
    return this.subscriptions.addSubscription(eventName, callback);
  }
}

export {createREPGraphic, REPGraphic};
